import { useId, useState } from "react";
import { skipToken } from "@reduxjs/toolkit/query";
import { SvgIcon } from "@progress/kendo-react-common";
import { Card, CardBody } from "@progress/kendo-react-layout";
import { chevronDownIcon, chevronRightIcon, dataIcon, infoCircleIcon } from "@progress/kendo-svg-icons";
import { useDataConfidenceRequest } from "../../../../hooks/useDataConfidenceRequest";
import { useGetDataConfidenceQuery } from "../../../../api/dataConfidenceApi";
import { formatDate, toDate } from "../../../../utils/dateValueConversion";
import { SovraSkeleton } from "../../../../components/skeleton/sovraSkeleton";

const clampScore = (score: number): number => Math.max(0, Math.min(100, Math.round(score)));

const DataConfidence: React.FC = () => {
    const [isAssessmentExpanded, setIsAssessmentExpanded] = useState(false);
    const assessmentId = useId();
    const request = useDataConfidenceRequest();
    const { data, isError, isFetching, isLoading } = useGetDataConfidenceQuery(request);
    const isUnavailable = request === skipToken;
    const confidenceScore = data ? clampScore(data.overall_score) : 0;

    return (
        <Card className="border border-light-subtle rounded-3 shadow-sm overflow-hidden min-w-0 w-100">
            <CardBody className="p-0">
                <div className="p-3 min-w-0">
                    <div className="d-flex align-items-start gap-2 mb-2 min-w-0">
                        <span
                            className="bg-success rounded-pill flex-shrink-0 mt-1"
                            style={{ width: "4px", minHeight: "24px" }}
                            aria-hidden="true"
                        />
                        <div className="min-w-0">
                            <h2 className="h6 fw-bold text-body mb-1">Data Confidence</h2>
                            <p className="small text-muted mb-0">Quality and completeness of input data</p>
                        </div>
                    </div>

                    <div className="border border-success-subtle bg-success-subtle rounded-3 p-2 px-sm-3 min-w-0">
                        {(isLoading || isFetching) && (
                            <div className="d-flex flex-wrap align-items-start align-items-sm-center justify-content-between gap-2 min-w-0">
                                <div className="flex-grow-1 min-w-0" style={{ flexBasis: "16rem" }}>
                                    <SovraSkeleton shape="text" style={{ width: 180, height: 20 }} />
                                    <SovraSkeleton shape="text" style={{ width: 96, height: 44 }} className="my-2" />
                                    <SovraSkeleton shape="text" style={{ width: "100%", height: 18 }} />
                                    <SovraSkeleton shape="text" style={{ width: "82%", height: 18 }} className="mt-2" />
                                </div>
                                <SovraSkeleton shape="circle" style={{ width: 64, height: 64 }} />
                            </div>
                        )}

                        {!isLoading && !isFetching && isUnavailable && (
                            <p className="mb-0 text-muted">Data confidence is unavailable until a commercial plan is selected.</p>
                        )}

                        {!isLoading && !isFetching && !isUnavailable && isError && (
                            <p className="mb-0 text-danger">Unable to load data confidence.</p>
                        )}

                        {!isLoading && !isFetching && !isUnavailable && !isError && data && (
                            <div className="d-flex flex-wrap align-items-start align-items-sm-center justify-content-between gap-2 min-w-0">
                                <div className="flex-grow-1 min-w-0" style={{ flexBasis: "16rem" }}>
                                    <p className="small text-body-secondary mb-1">Existing data confidence</p>
                                    <div className="h3 fw-semibold text-success mb-1">
                                        {confidenceScore}
                                        <span className="fs-6 fw-normal">/100</span>
                                        <span className="badge bg-success-subtle text-success-emphasis border border-success-subtle small ms-2">{data.confidence_level}</span>
                                    </div>
                                    <p className="small mb-1 text-break">
                                        Existing score combines coverage, recency, completeness, and alignment of sales, price,
                                        and promotion data.
                                    </p>
                                    <p className="small mb-0 text-break">
                                        Calculated separately from the three-year assessment below.
                                    </p>
                                </div>

                                <div
                                    className="rounded-circle d-inline-flex align-items-center justify-content-center flex-shrink-0"
                                    aria-label={`${confidenceScore} out of 100 existing data confidence`}
                                    role="img"
                                    style={{
                                        width: 64,
                                        height: 64,
                                        background: `conic-gradient(#008777 0 ${confidenceScore}%, #dfe7e6 ${confidenceScore}% 100%)`
                                    }}
                                >
                                    <span
                                        className="rounded-circle bg-white d-inline-flex align-items-center justify-content-center text-success"
                                        style={{ width: 48, height: 48 }}
                                    >
                                        <SvgIcon icon={dataIcon} size="large" />
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>
                    {!isLoading && !isFetching && !isError && data && (
                        <div className="border border-light-subtle bg-body-tertiary rounded-3 overflow-hidden mt-2">
                            <h3 className="mb-0">
                                <button
                                    id={`${assessmentId}-toggle`}
                                    type="button"
                                    className="btn btn-light rounded-0 w-100 text-start d-flex align-items-center justify-content-between gap-2 p-2 px-sm-3"
                                    aria-expanded={isAssessmentExpanded}
                                    aria-controls={assessmentId}
                                    onClick={() => setIsAssessmentExpanded(expanded => !expanded)}
                                >
                                    <span className="d-flex flex-column min-w-0">
                                        <span className="small fw-semibold">Three-Year Data Assessment</span>
                                        <span className="small text-body-secondary" style={{ fontSize: "0.75rem" }}>
                                            {isAssessmentExpanded ? "Hide coverage, weights and notes" : "View coverage, weights and notes"}
                                        </span>
                                    </span>
                                    <span className="d-flex align-items-center gap-2 flex-shrink-0">
                                        <span className="badge rounded-pill border border-success-subtle bg-success-subtle text-success-emphasis">
                                            {`${data.three_year_assessment.overall_score}/100 · ${data.three_year_assessment.confidence_level}`}
                                        </span>
                                        <SvgIcon icon={isAssessmentExpanded ? chevronDownIcon : chevronRightIcon} aria-hidden="true" />
                                    </span>
                                </button>
                            </h3>
                            <div
                                id={assessmentId}
                                role="region"
                                aria-labelledby={`${assessmentId}-toggle`}
                                hidden={!isAssessmentExpanded}
                                className="border-top p-2 px-sm-3"
                            >
                                <p className="small text-body-secondary mb-1">
                                    {`${formatDate(toDate(data.three_year_assessment.from_date)!)} to ${formatDate(toDate(data.three_year_assessment.to_date)!)} (inclusive)`}
                                </p>
                                <p className="small text-body-secondary mb-2">Coverage and availability averaged across selected products.</p>
                                <div className="row row-cols-1 row-cols-sm-2 row-cols-lg-3 g-2">
                                    {[
                                        {
                                            label: "Base price coverage",
                                            value: `${data.three_year_assessment.base_price_score}%`,
                                            description: "Days with a base price; current price counts across the window. Selling-price estimates earn half credit."
                                        },
                                        {
                                            label: "Current cost available",
                                            value: `${data.three_year_assessment.cost_of_goods_score}%`,
                                            description: "Selected products with a current cost; historical cost coverage is not measured."
                                        },
                                        {
                                            label: "Sales history",
                                            value: `${data.three_year_assessment.sales_score}%`,
                                            description: "Days covered by sales records with both revenue and units sold."
                                        },
                                        {
                                            label: "Promotion coverage",
                                            value: `${data.three_year_assessment.promotion_score}%`,
                                            description: "Promotion coverage for prices below 92% of base price. Scores 100% when no discounts are detected."
                                        },
                                        {
                                            label: "Retail media",
                                            value: "Not assessed",
                                            description: "Measurement is not implemented; currently contributes 0 of 20 possible points."
                                        },
                                        {
                                            label: "Three-year data score",
                                            value: `${data.three_year_assessment.overall_score}/100`,
                                            description: "Combined weighted score",
                                            level: data.three_year_assessment.confidence_level
                                        }
                                    ].map(metric => (
                                        <div
                                            key={metric.label}
                                            className="col d-flex flex-column align-items-center text-center min-w-0"
                                        >
                                            <span
                                                className={`badge rounded-pill text-wrap border px-2 py-1 mb-2 ${metric.level !== undefined
                                                    ? "border-success-subtle bg-success-subtle text-success-emphasis"
                                                    : "border-secondary-subtle bg-secondary-subtle text-secondary-emphasis"}`}
                                            >
                                                {metric.label}
                                            </span>
                                            <div
                                                className={`border rounded-3 px-3 py-2 w-100 d-flex flex-column align-items-center justify-content-center ${metric.level !== undefined
                                                    ? "border-success-subtle bg-success-subtle text-success-emphasis"
                                                    : "border-light-subtle bg-body"}`}
                                                style={{ minHeight: 76 }}
                                                aria-label={`${metric.label}: ${metric.value}`}
                                            >
                                                <span className="fs-4 fw-semibold">{metric.value}</span>
                                                {metric.level !== undefined && (
                                                    <span className="small">{metric.level}</span>
                                                )}
                                            </div>

                                            <div
                                                className="d-flex align-items-start gap-1 bg-secondary bg-opacity-10 rounded-2 px-2 py-1 mt-1 w-100 text-start text-body-secondary"
                                                style={{ fontSize: "0.7rem", lineHeight: 1.35 }}
                                            >
                                                <SvgIcon icon={infoCircleIcon} className="flex-shrink-0 mt-1" style={{ width: 12, height: 12 }} aria-hidden="true" />
                                                <p className="mb-0">{metric.description}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <div className="border border-light-subtle rounded-3 bg-body p-2 px-sm-3 mt-3">
                                    <div className="d-flex flex-wrap align-items-start gap-3">
                                        <div className="flex-grow-1" style={{ flexBasis: "12rem" }}>
                                            <h4 className="small fw-semibold mb-1">Contribution to total score</h4>
                                            <p className="small text-body-secondary mb-0">
                                                Each category can contribute up to the points shown, based on its score.
                                            </p>
                                            <p className="small text-body-secondary mt-1 mb-0">
                                                The maximum is currently 80/100 because retail media is not assessed.
                                            </p>
                                        </div>
                                        <dl className="d-flex flex-column gap-1 flex-grow-1 mb-0 small" style={{ flexBasis: "14rem" }}>
                                            {[
                                                { label: "Base price coverage", points: 10 },
                                                { label: "Current cost available", points: 10 },
                                                { label: "Sales history", points: 40 },
                                                { label: "Promotion coverage", points: 20 },
                                                { label: "Retail media (not assessed)", points: 20 }
                                            ].map(contribution => (
                                                <div key={contribution.label} className="d-flex align-items-center justify-content-between gap-2">
                                                    <dt className="fw-normal text-body-secondary">{contribution.label}</dt>
                                                    <dd className="badge rounded-pill bg-secondary-subtle text-secondary-emphasis mb-0">
                                                        {`${contribution.points} points`}
                                                    </dd>
                                                </div>
                                            ))}
                                        </dl>
                                    </div>
                                </div>
                                {data.three_year_assessment.warnings.length > 0 && (
                                    <div className="border border-warning-subtle bg-warning-subtle text-warning-emphasis rounded-2 px-2 py-1 mt-2">
                                        <ul className="small mb-0 ps-3">
                                            {data.three_year_assessment.warnings.map(warning => <li key={warning}>{warning}</li>)}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </CardBody>
        </Card>
    );
};

export default DataConfidence;
